(function () {
  'use strict';

  function waitForDeps(callback) {
    if (window.gsap && window.ScrollTrigger) {
      callback();
      return;
    }

    var interval = window.setInterval(function () {
      if (window.gsap && window.ScrollTrigger) {
        window.clearInterval(interval);
        callback();
      }
    }, 50);

    window.setTimeout(function () {
      window.clearInterval(interval);
    }, 8000);
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  // Preload an image and return a Promise that resolves to the Image element.
  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.decoding = 'async';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Failed to load ' + url)); };
      img.src = url;
    });
  }

  // Fetch the manifest JSON and return the ordered list of frame URLs.
  function fetchManifest(url) {
    return fetch(url, { credentials: 'omit' })
      .then(function (r) {
        if (!r.ok) throw new Error('manifest HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.ordered_urls)) return data.ordered_urls;
        if (data && data.frames && typeof data.frames === 'object') {
          return Object.keys(data.frames).sort().map(function (k) { return data.frames[k]; });
        }
        return [];
      });
  }

  // Preload images in parallel batches to avoid swamping the connection pool.
  function preloadAll(urls, batchSize, onProgress) {
    var images = new Array(urls.length);
    var loaded = 0;
    var idx = 0;

    function next() {
      if (idx >= urls.length) return Promise.resolve();
      var i = idx++;
      return loadImage(urls[i]).then(function (img) {
        images[i] = img;
        loaded++;
        if (typeof onProgress === 'function') onProgress(loaded, urls.length);
        return next();
      }).catch(function () {
        // Don't block the chain on a single failed frame.
        loaded++;
        if (typeof onProgress === 'function') onProgress(loaded, urls.length);
        return next();
      });
    }

    var workers = [];
    for (var i = 0; i < Math.min(batchSize, urls.length); i++) workers.push(next());
    return Promise.all(workers).then(function () { return images; });
  }

  function initScrollCinema(id) {
    var section = document.querySelector('[data-scroll-cinema-id="' + id + '"]');
    if (!section || section._scrollCinemaReady) return;
    section._scrollCinemaReady = true;

    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var pin = section.querySelector('.hp-scroll-cinema__pin');
    var canvas = section.querySelector('canvas.hp-scroll-cinema__canvas');
    var poster = section.querySelector('.hp-scroll-cinema__poster');
    var darkening = section.querySelector('[data-scroll-cinema-darkening]');
    var moments = Array.prototype.slice.call(section.querySelectorAll('[data-scroll-cinema-moment]'));
    var progressBar = section.querySelector('.hp-scroll-cinema__progress i');
    var snapCopy = section.dataset.snapCopy === 'true';
    var manifestUrl = section.dataset.frameManifest;

    if (!pin || moments.length === 0 || reducedMotion || !canvas || !manifestUrl) {
      moments.forEach(function (moment, index) {
        moment.classList.toggle('is-active', index === 0);
      });
      return;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;

    var ctx = canvas.getContext('2d', { alpha: false });
    var images = [];
    var lastFrame = -1;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var firstImage = null;

    function sizeCanvas() {
      var rect = canvas.getBoundingClientRect();
      var w = Math.max(1, Math.round(rect.width * dpr));
      var h = Math.max(1, Math.round(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
    }

    // Cover-fit (object-fit: cover) draw onto the canvas so frames fill
    // the box without distortion.
    function drawCover(img) {
      if (!img || !img.naturalWidth) return;
      var cw = canvas.width;
      var ch = canvas.height;
      var iw = img.naturalWidth;
      var ih = img.naturalHeight;
      var canvasRatio = cw / ch;
      var imgRatio = iw / ih;
      var sx, sy, sw, sh;
      if (imgRatio > canvasRatio) {
        // Image wider than canvas → crop sides
        sh = ih;
        sw = ih * canvasRatio;
        sx = (iw - sw) / 2;
        sy = 0;
      } else {
        // Image taller than canvas → crop top/bottom
        sw = iw;
        sh = iw / canvasRatio;
        sx = 0;
        sy = (ih - sh) / 2;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
    }

    function renderFrame(progress) {
      if (!images.length) return;
      var idx = clamp(Math.round(progress * (images.length - 1)), 0, images.length - 1);
      if (idx === lastFrame) return;
      var img = images[idx];
      if (!img) {
        // Image not yet loaded — fall back to nearest loaded frame
        for (var step = 1; step < images.length; step++) {
          if (images[idx - step]) { img = images[idx - step]; break; }
          if (images[idx + step]) { img = images[idx + step]; break; }
        }
      }
      if (img) {
        drawCover(img);
        lastFrame = idx;
      }
    }

    // Initial paint: draw the poster (or whatever is loaded) so we don't
    // show a blank canvas while frames are still streaming in.
    function paintPoster() {
      sizeCanvas();
      if (firstImage && firstImage.naturalWidth) {
        drawCover(firstImage);
      }
    }

    // GPU-promote the pin/canvas so transforms compose on the GPU layer.
    if (pin && pin.style) {
      pin.style.willChange = 'transform';
      pin.style.transform = 'translateZ(0)';
    }
    canvas.style.willChange = 'transform';
    canvas.style.transform = 'translateZ(0)';

    // Force translate3d for GPU promotion via CSS, no need for gsap.set.
    moments.forEach(function (m) {
      m.style.transform = 'translate3d(0, 0, 0)';
      m.style.willChange = 'opacity';
    });
    if (progressBar) progressBar.style.transform = 'scaleX(0)';

    var triggers = [];
    var resizeRaf = null;

    // Pre-compute caption fade ranges from progress (0-1). Each moment owns
    // an even slice of the scroll, with a small crossfade band at each
    // boundary. This is computed up-front and used inside onUpdate.
    function computeCaptionRanges(count) {
      // For 2 moments: moment 0 owns 0-0.50, moment 1 owns 0.50-1.0.
      // Crossfade band of 0.05 around the midpoint(s).
      var fade = 0.05;
      var ranges = [];
      for (var i = 0; i < count; i++) {
        var segStart = i / count;
        var segEnd = (i + 1) / count;
        ranges.push({
          // Visible window: from `inStart` (start of fade-in) to `outEnd` (end of fade-out)
          inStart: i === 0 ? -1 : segStart - fade * 0.5,
          inEnd: i === 0 ? 0 : segStart + fade * 0.5,
          outStart: i === count - 1 ? 2 : segEnd - fade * 0.5,
          outEnd: i === count - 1 ? 2 : segEnd + fade * 0.5
        });
      }
      return ranges;
    }

    var captionRanges = computeCaptionRanges(moments.length);

    function applyCaptionAlphas(progress) {
      for (var i = 0; i < moments.length; i++) {
        var r = captionRanges[i];
        var alpha;
        if (progress < r.inStart) alpha = 0;
        else if (progress < r.inEnd) alpha = (progress - r.inStart) / Math.max(r.inEnd - r.inStart, 0.0001);
        else if (progress < r.outStart) alpha = 1;
        else if (progress < r.outEnd) alpha = 1 - (progress - r.outStart) / Math.max(r.outEnd - r.outStart, 0.0001);
        else alpha = 0;
        alpha = Math.max(0, Math.min(1, alpha));
        var m = moments[i];
        m.style.opacity = alpha.toFixed(3);
        m.style.visibility = alpha > 0.001 ? 'visible' : 'hidden';
        m.style.pointerEvents = alpha > 0.5 ? 'auto' : 'none';
      }
    }

    function buildTimeline() {
      // ScrollTrigger drives EVERYTHING via direct progress mapping. No GSAP
      // timeline tweens for captions — the timeline-duration trap (where the
      // timeline auto-shrinks to its last tween) was making positions of 0.5
      // map to scroll progress 0.91. Direct progress→opacity math removes
      // the entire problem.
      var st = ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.08,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        fastScrollEnd: true,
        onUpdate: function (self) {
          var p = self.progress;
          renderFrame(p);
          applyCaptionAlphas(p);
          if (progressBar) progressBar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
          if (darkening && darkening.dataset.enabled === 'true') {
            // 0% from 0-50% scroll, ramps to 50% from 50-100% scroll
            var d = (p - 0.50) / 0.50;
            d = Math.max(0, Math.min(1, d));
            darkening.style.opacity = (d * 0.50).toFixed(3);
          }
          // Scroll cue: stay visible the whole way through the cinema, fade out
          // only in the last 5% so it gracefully disappears as the section exits.
          var cueOpacity = p >= 0.95 ? 0.78 * (1 - (p - 0.95) / 0.05) : 0.78;
          section.style.setProperty('--sc-cue-opacity', Math.max(0, cueOpacity).toFixed(3));
        }
      });

      // Initial state at progress 0
      applyCaptionAlphas(0);
      renderFrame(0);

      triggers.push(st);
      ScrollTrigger.refresh();
    }

    function onResize() {
      if (resizeRaf) return;
      resizeRaf = window.requestAnimationFrame(function () {
        resizeRaf = null;
        sizeCanvas();
        lastFrame = -1; // force re-render at new size
        var st = triggers[0];
        if (st) renderFrame(st.progress);
        else if (firstImage) drawCover(firstImage);
      });
    }
    window.addEventListener('resize', onResize, { passive: true });

    // Build the ScrollTrigger NOW, synchronously, before any async work.
    // This guarantees captions transition correctly even if image loading
    // stalls. The canvas just shows blank/poster until frames arrive.
    sizeCanvas();
    buildTimeline();

    // Then load the manifest and stream in frames in the background.
    fetchManifest(manifestUrl).then(function (urls) {
      if (!urls.length) {
        console.warn('[hp-scroll-cinema] empty manifest', manifestUrl);
        return;
      }
      images = new Array(urls.length);
      return loadImage(urls[0]).then(function (img) {
        images[0] = img;
        firstImage = img;
        paintPoster();
        if (poster) poster.style.opacity = '0';
        // Re-render at current scroll progress now that frame 0 is loaded
        var st = triggers[0];
        if (st) renderFrame(st.progress);

        // Preload remaining frames in parallel, 6 at a time
        preloadAll(urls.slice(1), 6).then(function (loaded) {
          for (var i = 0; i < loaded.length; i++) images[i + 1] = loaded[i];
        });
      });
    }).catch(function (err) {
      console.warn('[hp-scroll-cinema] manifest/image failed:', err);
    });

    section._scrollCinemaCleanup = function () {
      triggers.forEach(function (trigger) {
        if (trigger) trigger.kill();
      });
      window.removeEventListener('resize', onResize);
      triggers = [];
      images = [];
      section._scrollCinemaReady = false;
    };
  }

  function boot() {
    var sections = document.querySelectorAll('[data-scroll-cinema-id]');
    // Fallback for browsers without IntersectionObserver: init immediately.
    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (section) {
        waitForDeps(function () { initScrollCinema(section.dataset.scrollCinemaId); });
      });
      return;
    }
    // Lazy-init: only fetch the manifest + preload frames when the section is
    // within ~1.5 viewports of being visible. Saves ~12MB of frame imagery on
    // page load for the second cinema (which is at section 10/12).
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var section = entry.target;
        observer.unobserve(section);
        waitForDeps(function () { initScrollCinema(section.dataset.scrollCinemaId); });
      });
    }, { rootMargin: '150% 0px 150% 0px', threshold: 0 });
    sections.forEach(function (section) { observer.observe(section); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-scroll-cinema-id]');
    if (!section) return;
    waitForDeps(function () {
      initScrollCinema(section.dataset.scrollCinemaId);
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.querySelector('[data-scroll-cinema-id]');
    if (section && section._scrollCinemaCleanup) {
      section._scrollCinemaCleanup();
    }
  });
})();
